import { Contact, IContact } from '@app/models/Contact'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IGuestListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IContact>
  readonly totalItems: number
}

const GuestListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Contact>(),
  totalItems: 0,
})

export class GuestListState extends GuestListStateRecord
  implements IGuestListState {
  public static fromJS(initialState?: Partial<IGuestListState>) {
    if (!initialState) {
      return new GuestListState()
    }

    const { items, ...rest } = initialState
    return new GuestListState({
      items: items ? List(items.map((i) => Contact.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Contact>
  public readonly totalItems: number

  constructor(props?: Partial<GuestListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum GuestListActionTypes {
  LoadGuest = 'GUEST_LIST::LOAD_GUEST',
  LoadGuestFailure = 'GUEST_LIST::LOAD_GUEST_FAILURE',
  LoadGuestRequest = 'GUEST_LIST::LOAD_GUEST_REQUEST',
  LoadGuestSuccess = 'GUEST_LIST::LOAD_GUEST_SUCCESS',
  SetConnectionType = 'GUEST_LIST::SET_CONNECTION_TYPE',
  SetFilterText = 'GUEST_LIST::SET_FILTER_TEXT',
  SetScope = 'GUEST_LIST::SET_SCOPE',
  SetSortOrder = 'GUEST_LIST::SET_SORT_ORDER',
}

interface ILoadGuestAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: GuestListActionTypes.LoadGuest
}

interface ILoadGuestFailureAction {
  error: string
  type: GuestListActionTypes.LoadGuestFailure
}

interface ILoadGuestRequestAction {
  type: GuestListActionTypes.LoadGuestRequest
}

interface ILoadGuestSuccessAction {
  append: boolean
  items: Contact[]
  totalItems: number
  type: GuestListActionTypes.LoadGuestSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: GuestListActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadGuestFailureAction
  | ILoadGuestRequestAction
  | ILoadGuestSuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadGuest(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadGuestAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: GuestListActionTypes.LoadGuest,
  }
}

function loadGuestFailure(error: string): ILoadGuestFailureAction {
  return {
    error,
    type: GuestListActionTypes.LoadGuestFailure,
  }
}

function loadGuestRequest(): ILoadGuestRequestAction {
  return {
    type: GuestListActionTypes.LoadGuestRequest,
  }
}

function loadGuestSuccess(
  items: Contact[],
  append: boolean,
  totalItems: number,
): ILoadGuestSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: GuestListActionTypes.LoadGuestSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: GuestListActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadGuest,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<GuestListState> = (
  state: GuestListState = new GuestListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case GuestListActionTypes.LoadGuestFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as GuestListState

    case GuestListActionTypes.LoadGuestRequest:
      return state.set('isLoading', true) as GuestListState

    case GuestListActionTypes.LoadGuestSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Contact>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as GuestListState

    case GuestListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as GuestListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadGuestSaga(action: ILoadGuestAction) {
  try {
    yield put(loadGuestRequest())

    const currentState: GuestListState = yield select(
      (state: ApplicationState) => state.guestList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    query.push(`sort=Name`)

    const baseUrl = 'api/guests'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Contact[] = result.results.map((c: any) =>
        Contact.fromJS(c),
      )
      yield put(loadGuestSuccess(contacts, action.append, result.paging.total))
    } else {
      yield put(loadGuestFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadGuestFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadGuest() {
  yield takeLatest(GuestListActionTypes.LoadGuest, loadGuestSaga)
}

export function* guestListRootSaga() {
  yield fork(watchLoadGuest)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): GuestListState =>
  state.guestList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
